/**
 * ContactPanelTasks - Seção de tarefas do painel lateral de contato.
 * Usa o hook global useTaskManagement para ações e o Sheet para criação de novas tarefas.
 */
import React, { useId, useState } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { Task } from '../../../../types';
import { TaskItem } from './TaskItem';
import { useTaskManagement } from '../../../../features/tasks/hooks/useTaskManagement';

interface ContactPanelTasksProps {
    isOpen: boolean;
    onToggle: () => void;
    tasks: Task[];
    isCreatingTask: boolean;
    currentLead?: Task['leadId'] extends string ? { id: string } : never;
    onCreateTask: (taskData: Partial<Task>) => void;
    onCancelCreate: () => void;
    onStartCreateTask: () => void;
    onToggleTaskCompletion: (taskId: string) => void;
    onUpdateTask: (task: Task) => void;
    onDeleteTask: (taskId: string) => void;
    onDuplicateTask: (task: Task) => void;
}

export const ContactPanelTasks: React.FC<ContactPanelTasksProps> = ({
    isOpen,
    onToggle,
    tasks,
    isCreatingTask,
    onCreateTask,
    onCancelCreate,
    onStartCreateTask
}) => {
    const panelId = useId();
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');

    const {
        tasks: filteredTasks,
        toggleTaskCompletion,
        saveTask,
        deleteTask,
        duplicateTask
    } = useTaskManagement(tasks);

    const handleSubmit = () => {
        if (!title.trim()) return;
        const dueDate = date ? (time ? `${date}T${time}` : date) : undefined;
        onCreateTask({ title, dueDate });
        setTitle('');
        setDate('');
        setTime('');
    };

    const handleCancel = () => {
        setTitle('');
        setDate('');
        setTime('');
        onCancelCreate();
    };

    return (
        <section>
            <button
                type="button"
                onClick={onToggle}
                className="flex min-h-11 w-full items-center justify-between px-3 py-2"
                aria-expanded={isOpen}
                aria-controls={panelId}
            >
                <div className="flex items-center gap-2 chat-section-heading">
                    <span>Tarefas</span>
                    <span className="chat-count rounded-full border border-border bg-surface-subtle px-1.5 py-0 leading-5 text-muted">
                        {tasks.length}
                    </span>
                </div>
                <AppIcon
                    name="chevron-down"
                    className={`chat-accordion-chevron ${isOpen ? '' : 'chat-accordion-chevron--closed'}`}
                    aria-hidden="true"
                />
            </button>

            {isOpen && (
                <div id={panelId} className="bg-card px-3 pb-3">
                    {isCreatingTask ? (
                        <form
                            className="space-y-4 rounded-xl border border-border bg-surface p-4"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                        >
                            <div className="grid gap-1.5">
                                <label htmlFor="contact-panel-task-title" className="text-label">
                                    Título da tarefa
                                </label>
                                <input
                                    id="contact-panel-task-title"
                                    name="taskTitle"
                                    type="text"
                                    placeholder="Ex: Ligar para cliente"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="input min-h-11 w-full text-base sm:text-sm"
                                    autoFocus
                                    autoComplete="off"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div className="grid gap-1.5">
                                    <label htmlFor="contact-panel-task-date" className="text-label">
                                        Data
                                    </label>
                                    <input
                                        id="contact-panel-task-date"
                                        name="taskDate"
                                        type="date"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        className="input min-h-11 w-full font-mono text-base sm:text-sm"
                                    />
                                </div>
                                <div className="grid gap-1.5">
                                    <label htmlFor="contact-panel-task-time" className="text-label">
                                        Hora
                                    </label>
                                    <input
                                        id="contact-panel-task-time"
                                        name="taskTime"
                                        type="time"
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                        className="input min-h-11 w-full font-mono text-base sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    className="min-h-11 flex-1 rounded-lg bg-primary text-body-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                                >
                                    Criar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="min-h-11 flex-1 rounded-lg border border-border bg-surface text-body-sm font-medium text-secondary transition-colors hover:bg-surface-subtle"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </form>
                    ) : (
                        <>
                            {filteredTasks.length > 0 && (
                                <ul className="mb-3 list-none space-y-2 p-0">
                                    {filteredTasks.map(task => (
                                        <li key={task.id}>
                                            <TaskItem
                                                task={task}
                                                onToggleCompletion={(id) => {
                                                    const t = tasks.find(x => x.id === id);
                                                    if (t) toggleTaskCompletion(t);
                                                }}
                                                onUpdate={(t) => saveTask(t, t.id)}
                                                onDelete={(id) => deleteTask(id)}
                                                onDuplicate={(t) => duplicateTask(t)}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <button
                                type="button"
                                onClick={onStartCreateTask}
                                className={
                                    filteredTasks.length === 0
                                        ? 'chip chip--dashed min-h-11 w-full max-w-full justify-center rounded-lg px-3'
                                        : 'chip chip--dashed mt-1 min-h-11 w-full max-w-full justify-center rounded-lg px-3'
                                }
                            >
                                <AppIcon name="plus" className="size-4 shrink-0" aria-hidden="true" />
                                <span className="min-w-0 truncate">Criar uma tarefa</span>
                            </button>
                        </>
                    )}
                </div>
            )}
        </section>
    );
};
